/**
 * In-app select: replaces a native <select> with a button + popover list so
 * dropdowns match the app instead of the browser. The <select> stays in the
 * DOM (hidden) as the form value and the source of options — code that
 * rewrites its options or sets .value just calls syncSelect(sel) afterwards.
 *
 * The list is position:fixed next to its button (캘린더 툴바 2차, 2026-09-30): inside a
 * scrolling panel (the property manager is overflow:auto) an absolute list was cut off at
 * the panel's edge. It stays a child of the wrapper so every `wrap.contains()` /
 * `.closest('.cal-sel-pop')` check keeps working, opens upward when there is more room
 * above, and follows its button when something outside it scrolls or the window resizes
 * (closing only once the button has left the screen — a smooth scroll that is still
 * settling when the list opens must not shut it).
 */
let openPop = null;
let openFor = null;

// Where the list sits: under the button, or above it when the space below is short
// and there is more above; never taller than the room it opens into.
export function selectPopPlacement(btn, pop, vw, vh) {
    const gap = 4;
    const edge = 8;
    const below = vh - btn.bottom - gap - edge;
    const above = btn.top - gap - edge;
    const want = Math.min(pop.natural, pop.cap);
    const up = want > below && above > below;
    const room = Math.max(96, up ? above : below);
    const height = Math.min(want, room);
    const width = Math.max(pop.width, btn.width);
    const left = Math.max(edge, Math.min(btn.left, vw - width - edge));
    const top = up ? btn.top - gap - height : btn.bottom + gap;
    return { left, top, maxHeight: Math.min(pop.cap, room), up };
}

function placePop(pop, btn) {
    const r = btn.getBoundingClientRect();
    pop.style.minWidth = Math.max(Math.round(r.width), pop._minW) + 'px';
    pop.style.maxHeight = pop._cap + 'px';
    const p = selectPopPlacement(
        { top: r.top, bottom: r.bottom, left: r.left, width: r.width },
        { natural: pop.offsetHeight, cap: pop._cap, width: pop.offsetWidth },
        document.documentElement.clientWidth, window.innerHeight,
    );
    pop.style.maxHeight = p.maxHeight + 'px';
    pop.classList.toggle('is-up', p.up);
    const top = p.up ? r.top - 4 - pop.offsetHeight : p.top;
    // A transformed ancestor (the event panel while it slides in) becomes the containing
    // block of a fixed box — _dx/_dy is where (0, 0) really landed, measured at open.
    pop.style.left = (p.left - pop._dx) + 'px';
    pop.style.top = (top - pop._dy) + 'px';
}

let placeRaf = 0;
function onOutsideScroll(e) {
    if (!openPop || placeRaf || (e && e.target instanceof Node && openPop.contains(e.target))) return;
    placeRaf = requestAnimationFrame(() => {
        placeRaf = 0;
        if (!openPop || !openFor) return;
        const r = openFor.getBoundingClientRect();
        if (!openFor.isConnected || (!r.width && !r.height) || r.bottom < 0 || r.top > window.innerHeight) {
            closeInappSelect();
            return;
        }
        placePop(openPop, openFor);
    });
}

const CHEVRON = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>';

export function closeInappSelect() {
    if (openPop) {
        openPop.remove();
        openPop = null;
        if (openFor) openFor.setAttribute('aria-expanded', 'false');
        openFor = null;
        window.removeEventListener('scroll', onOutsideScroll, true);
        window.removeEventListener('resize', onOutsideScroll);
        if (placeRaf) cancelAnimationFrame(placeRaf);
        placeRaf = 0;
    }
}

export function isInappSelectOpen() {
    return !!openPop;
}

// Optional icon in front of each option in the list (the property type picker):
// fn(value) returns trusted SVG markup — module constants only, never user text.
export function setOptionIcons(sel, fn) {
    if (sel) sel._optIcon = fn;
}

export function syncSelect(sel) {
    if (sel) sel.dispatchEvent(new Event('inapp-sync'));
}

export function enhanceSelect(sel) {
    if (!sel || sel.dataset.inappEnhanced) return;
    sel.dataset.inappEnhanced = '1';
    const wrap = document.createElement('span');
    wrap.className = 'cal-sel';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cal-sel-btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    const label = document.createElement('span');
    btn.appendChild(label);
    btn.insertAdjacentHTML('beforeend', CHEVRON);
    sel.parentNode.insertBefore(wrap, sel);
    wrap.appendChild(sel);
    wrap.appendChild(btn);
    sel.hidden = true;
    sel.style.display = 'none';
    sel.tabIndex = -1;

    function sync() {
        const o = sel.selectedOptions[0];
        label.textContent = o ? o.textContent : (sel.dataset.placeholder || '');
        btn.disabled = sel.disabled;
    }
    sel.addEventListener('change', sync);
    sel.addEventListener('inapp-sync', sync);

    btn.addEventListener('click', () => {
        if (openFor === btn) {
            closeInappSelect();
            return;
        }
        closeInappSelect();
        const pop = document.createElement('div');
        pop.className = 'cal-sel-pop';
        pop.setAttribute('role', 'listbox');
        Array.from(sel.options).forEach((o) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'cal-sel-opt' + (o.selected ? ' sel' : '');
            b.textContent = o.textContent;
            const icon = sel._optIcon ? sel._optIcon(o.value) : '';
            if (icon) {
                const i = document.createElement('i');
                i.className = 'cal-sel-icon';
                i.setAttribute('aria-hidden', 'true');
                i.innerHTML = icon;
                b.prepend(i);
            }
            b.setAttribute('role', 'option');
            b.addEventListener('click', () => {
                sel.value = o.value;
                closeInappSelect();
                sel.dispatchEvent(new Event('change', { bubbles: true }));
            });
            pop.appendChild(b);
        });
        pop.classList.add('is-fixed');
        pop.style.animation = 'none'; // measure the landed position without the entrance transform
        wrap.appendChild(pop);
        const cs = getComputedStyle(pop);
        pop._cap = parseFloat(cs.maxHeight) || 240;
        pop._minW = parseFloat(cs.minWidth) || 0;
        pop._dx = 0;
        pop._dy = 0;
        pop.style.left = '0px';
        pop.style.top = '0px';
        const origin = pop.getBoundingClientRect();
        pop._dx = origin.left;
        pop._dy = origin.top;
        placePop(pop, btn);
        pop.style.animation = '';
        openPop = pop;
        openFor = btn;
        btn.setAttribute('aria-expanded', 'true');
        // scrollIntoView would also scroll the panels around the button — move the list only
        const cur = pop.querySelector('.sel');
        if (cur) pop.scrollTop = Math.max(0, cur.offsetTop - (pop.clientHeight - cur.offsetHeight) / 2);
        window.addEventListener('scroll', onOutsideScroll, true);
        window.addEventListener('resize', onOutsideScroll);
    });
    sync();
}

export function initInappSelects(root) {
    (root || document).querySelectorAll('select[data-inapp-select]').forEach(enhanceSelect);
}

document.addEventListener('mousedown', (e) => {
    if (openPop && !openPop.contains(e.target) && !(openFor && openFor.contains(e.target))) closeInappSelect();
}, true);
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openPop) {
        closeInappSelect();
        e.stopImmediatePropagation();
    }
}, true);
